import {
  Stack,
  Tooltip,
  Accordion,
  AccordionSummary,
  Typography,
  AccordionDetails,
} from '@mui/material';
import { useEffect, useState } from 'react';

type Props = {
  label: React.ReactNode;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  controlledExpanded?: boolean;
  disabled?: boolean;
  onExpandChange?: (expanded: boolean) => void;
  tooltip?: React.ReactNode;
  disableGutters?: boolean; // removes margint top and bottom
  syncDisabled?: boolean; // when true closes accordion if disabled is also true
  passThrough?: boolean; // when true render only children without accordion
  backgroundColor?: string;
  size?: 'small' | 'default';
};

export const GroupCollapse = ({
  label,
  defaultExpanded,
  children,
  controlledExpanded,
  disabled,
  onExpandChange,
  tooltip,
  disableGutters = true,
  syncDisabled = true,
  passThrough,
  backgroundColor,
  size = 'default',
}: Props) => {
  const [internalExpanded, setInternalExpanded] = useState(!!defaultExpanded);

  const isControlled = controlledExpanded !== undefined;
  const isExpanded = isControlled ? controlledExpanded : internalExpanded;

  useEffect(() => {
    if (!disabled || !syncDisabled) return;

    if (isControlled) {
      onExpandChange?.(false);
    } else {
      setInternalExpanded(false);
    }
  }, [disabled, isControlled, onExpandChange, syncDisabled]);

  useEffect(() => {
    if (!isControlled && defaultExpanded !== undefined) {
      setInternalExpanded(!!defaultExpanded);
    }
  }, [defaultExpanded, isControlled]);

  if (passThrough) return <>{children}</>;

  const padding = size === 'small' ? '14px' : '18px';
  const fontSize = size === 'small' ? '14px' : '16px'; // Assuming 16px is your default font size

  return (
    <Accordion
      expanded={isExpanded}
      disabled={disabled}
      onChange={(_e, expanded) => {
        onExpandChange?.(expanded);

        if (!isControlled) {
          setInternalExpanded(expanded);
        }
      }}
      sx={{
        borderRadius: '8px !important',
        border: '1px solid var(--Gray-300, #D0D5DD)',
        my: disableGutters ? '0 !important' : undefined,
        boxShadow: 'none',
        backgroundColor: backgroundColor || '#fff', // Use prop or default
        color: '#000', // Use prop or default
        '.MuiAccordionSummary-root': {
          minHeight: '48px !important',
        },

        '.MuiAccordionSummary-content': {
          margin: '0 !important',
          alignItems: 'center',
          justifyContent: 'space-between',
        },
        '&.Mui-expanded': {
          borderColor: '#E0E0E0',
        },

        '&::before': {
          display: 'none',
        },
      }}
    >
      <AccordionSummary
        sx={{ padding }}
        expandIcon={
          <i
            className="fa-solid fa-chevron-down fa-sm"
            style={{
              color: '#667085',
              padding: '4px',
            }}
          />
        }
      >
        <Stack direction="row" alignItems="center" gap="4px">
          {typeof label === 'string' ? (
            <Typography
              sx={{
                color: 'var(--Black, #000)',
                fontFamily: 'Inter',
                fontSize,
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '24px',
              }}
            >
              {label}
            </Typography>
          ) : (
            label
          )}
          {tooltip && (
            <Tooltip title={tooltip} arrow>
              <i
                className="fa-regular fa-circle-info fa-sm"
                style={{
                  cursor: 'help',
                  color: '#828282',
                  padding: '2px',
                }}
                onClick={(e) => e.stopPropagation()}
              />
            </Tooltip>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ padding, paddingTop: '0px !important' }}>{children}</AccordionDetails>
    </Accordion>
  );
};
